import { useState, type ReactNode } from "react";
import { Link } from "react-router";
import { useStaffWorkspace } from "../../../app/session";
import { api } from "../../../data";
import { Money, PillView, SortTh, SortWahl, useSorted } from "../../../components/ui";
import { useArbeitsbereich, useUpgradeZiel } from "../../../components/Tarif";
import { LineChart } from "../../../components/charts";
import { date, money, monthShort, numberLocale, shortAmount } from "../../../lib/format";
import { actionLabel, type InvoiceView, type Pill } from "../../../model/invoice-view";
import { balanceCents } from "../../../model/invoice-activity";
import {
  BOOKING_KIND, bookingPill, contractPill, DEAL_MODEL, INVOICE_KIND, matchesDealFilter, platformsLabel, termLabel,
  type BookingRow, type ContractRow, type DealFilter, type DealLine,
} from "./model";
import "./stammdaten.css";
import { t } from "../../../lib/sprache";
import { VertragVorschau, useVorschauParam } from "../vertraege/VertragVorschau";

/** Kleine Bausteine der Stammdaten-Seiten, alle mit Markup und Klassen des Design-Pakets. */

export const RIGHTS_TEXT = () => t("Diese Aktion ist für dein Konto nicht freigegeben. Wende dich an deinen Admin.", "This action is not enabled for your account. Contact your admin.");

export function DemoNote() {
  return api.mode === "demo" ? <p className="demo-note">{t("Demo · Alle Daten sind erfunden.", "Demo · All data is fictional.")}</p> : null;
}

/** KPI-Kachel wie auf der Übersicht (Beschriftung, optional Zeitraum-Chip, Wert, Basis). */
export function Kpi({ label, chip, value, sub, alert }: { label: string; chip?: string; value: ReactNode; sub?: ReactNode; alert?: ReactNode }) {
  return (
    <div className="kpi">
      <small>{label}{chip && <> <span className="kpi-chip">{chip}</span></>}</small>
      <div className="metric num">{value}</div>
      {sub !== undefined && <small>{sub}</small>}
      {alert && <div className="kpi-alert">{alert}</div>}
    </div>
  );
}

/** Zeile „Beschriftung – Wert“ wie im Paket (deal-detail, Aufteilung). */
export function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="task">
      <span>{label}</span>
      <strong>{children}</strong>
    </div>
  );
}

export function FieldError({ id, text }: { id: string; text?: string }) {
  return text ? <small id={id} className="tk-field-error">{text}</small> : null;
}

/** Zweistufige Bestätigung im Seitenfluss (irreversible Aktion präzise bestätigen, 08_komponenten Modal). */
export function ConfirmAction({ label, question, confirmLabel, onConfirm, busy, disabled, describedBy, danger }: {
  label: string; question: string; confirmLabel: string; onConfirm: () => Promise<unknown>; busy?: boolean; disabled?: boolean; describedBy?: string; danger?: boolean;
}) {
  const [asking, setAsking] = useState(false);
  if (!asking) {
    return <button type="button" className={danger ? "danger" : ""} disabled={disabled} aria-describedby={describedBy} onClick={() => setAsking(true)}>{label}</button>;
  }
  return (
    <div className="notice stack" role="group" aria-label={label}>
      <p>{question}</p>
      <div className="row">
        <button type="button" className={danger ? "danger" : "primary"} disabled={busy} aria-busy={busy} onClick={async () => { await onConfirm(); setAsking(false); }}>
          {busy ? t("Wird gespeichert …", "Saving …") : confirmLabel}
        </button>
        <button type="button" onClick={() => setAsking(false)}>{t("Abbrechen", "Cancel")}</button>
      </div>
    </div>
  );
}

/** Link zum Weitergeben mit Kopierknopf (Einladung, Insights-Freigabe). */
export function CopyLink({ label, url }: { label: string; url: string }) {
  const [copied, setCopied] = useState<"ok" | "fail" | null>(null);
  return (
    <>
      {/* Prüf-Chat Nachtest (03.11.2026), R-06: der Link steht als lesbarer Text da (auch für Vorlesen, Seitentext und Kopieren von
          Hand), nicht nur als Wert eines Eingabefelds; ein Klick markiert ihn ganz */}
      <div className="stack tk-gap-sm">
        <span className="tk-copylink-label">{label}</span>
        <output className="tk-copylink-url" tabIndex={0} aria-label={label} onFocus={(e) => window.getSelection()?.selectAllChildren(e.currentTarget)}>{url}</output>
      </div>
      <div className="row">
        <button
          type="button"
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(url);
              setCopied("ok");
            } catch {
              setCopied("fail");
            }
          }}
        >
          {t("Link kopieren", "Copy link")}</button>
        {copied === "ok" && <span role="status">{t("Link kopiert.", "Link copied.")}</span>}
        {copied === "fail" && <span role="status">{t("Kopieren nicht möglich. Markiere den Link und kopiere ihn von Hand.", "Copying not possible. Select the link and copy it by hand.")}</span>}
      </div>
    </>
  );
}

// ── Umsatzverlauf: gemeinsames Liniendiagramm (components/charts), zeichnet in echten Pixeln der Kartenbreite ──────────
// Früher ein festes SVG mit viewBox 930×250, das in der halben Karte auf 3–7 px Schrift schrumpfte (SICHT-AGENTUR-REST-17);
// jetzt dieselbe Grafik wie auf der Übersicht, mit lesbaren Achsen, Hervorheben in der Legende und „Werte als Tabelle“.

function short(cents: number): string {
  return shortAmount(cents);
}

export interface Series { label: string; values: number[] }
const SERIES_COLORS = ["var(--accent)", "var(--info)", "var(--tk-s3)"] as const;

export function RevenueLines({ title, months, series, currency }: { title: string; months: string[]; series: Series[]; currency: string }) {
  return (
    <LineChart
      labels={months.map((m) => monthShort(m))}
      series={series.map((s, i) => ({ key: `s${i}`, label: s.label, values: s.values, color: SERIES_COLORS[i] ?? "var(--tk-s4)", fill: i === 0 }))}
      format={(v) => money(v, currency)}
      short={short}
      caption={title}
      // D-1: niedriger (180 statt 240 px), die Werte stehen zusätzlich als Tabelle bereit
      height={180}
      compact
    />
  );
}

/** Rangliste mit Balken (.rank-row aus der Creator-Übersicht des Pakets). */
export function RankRows({ rows, empty }: { rows: { key: string; label: ReactNode; value: number; display: string }[]; empty: string }) {
  if (!rows.length) return <p className="muted">{empty}</p>;
  const max = Math.max(0, ...rows.map((r) => r.value));
  return (
    <>
      {rows.map((r) => (
        <div key={r.key} className="rank-row">
          <span>{r.label}</span>
          <div className="rank-track">
            <div className="rank-fill" style={{ width: `${max > 0 ? Math.max(2, Math.round((r.value / max) * 100)) : 0}%` }}></div>
          </div>
          <strong className="num">{r.display}</strong>
        </div>
      ))}
    </>
  );
}

// ── Kooperationen (Tabelle wie creator-profil.html / marken-detail.html) ─────────────────────────

const DEAL_FILTERS: { key: DealFilter; label: string }[] = [
  { key: "alle", get label() { return t("Alle", "All"); } },
  { key: "aktiv", get label() { return t("Aktiv", "Active"); } },
  { key: "abgeschlossen", get label() { return t("Abgeschlossen", "Completed"); } },
];

export function DealTable({ lines, partner, caption, empty }: { lines: DealLine[]; partner: "brand" | "talent"; caption: string; empty: string }) {
  const { amounts } = useSicht();
  const [filter, setFilter] = useState<DealFilter>("alle");
  const shown = lines.filter((l) => matchesDealFilter(l, filter));
  const { sorted, sort, toggle } = useSorted(shown, {
    partner: (l) => (partner === "brand" ? l.brandName : l.talentName),
    titel: (l) => l.deal.title,
    status: (l) => l.pill.label,
    laufzeit: (l) => l.months[0] ?? l.deal.start_month,
    frist: (l) => l.nextDeadline,
    plattformen: (l) => platformsLabel(l.deal.platforms),
    honorar: (l) => l.totalCents,
  });
  return (
    <>
      <div className="toolbar">
        {DEAL_FILTERS.map((f) => (
          <button key={f.key} className="" aria-pressed={filter === f.key} onClick={() => setFilter(f.key)}>
            {f.label} · {lines.filter((l) => matchesDealFilter(l, f.key)).length}
          </button>
        ))}
      </div>
      <SortWahl />
      <div className="tablewrap tk-cardtable">
        <table>
          <caption className="sr-only">{caption}</caption>
          <thead>
            <tr>
              <SortTh label={partner === "brand" ? t("Marke", "Brand") : "Creator"} k="partner" sort={sort} toggle={toggle} />
              <SortTh label={t("Kooperation", "Deal")} k="titel" sort={sort} toggle={toggle} />
              <SortTh label="Status" k="status" sort={sort} toggle={toggle} />
              <SortTh label={t("Laufzeit", "term")} k="laufzeit" sort={sort} toggle={toggle} />
              <SortTh label={t("Nächste Frist", "Next deadline")} k="frist" sort={sort} toggle={toggle} />
              <SortTh label={t("Plattformen", "Platforms")} k="plattformen" sort={sort} toggle={toggle} />
              {amounts && <SortTh label={t("Honorar netto", "Net fee")} k="honorar" sort={sort} toggle={toggle} money />}
            </tr>
          </thead>
          <tbody>
            {sorted.map((l) => {
              const other = partner === "brand" ? { name: l.brandName, to: `/agentur/marken/${l.deal.brand_id}` } : { name: l.talentName, to: `/agentur/creator/${l.deal.talent_id}` };
              const terminal = ["done", "cancelled", "blocked"].includes(l.deal.status) && !l.deal.archived_at;
              return (
                <tr key={l.deal.id}>
                  <td data-label={partner === "brand" ? t("Marke", "Brand") : "Creator"}><Link to={other.to}><strong>{other.name}</strong></Link></td>
                  <td data-label={t("Kooperation", "Deal")} className="tk-titel-zelle">
                    <Link to={`/agentur/kooperationen/${l.deal.id}${l.focusMonth ? `?monat=${l.focusMonth}` : ""}`} title={l.deal.title}>{l.deal.title}</Link>
                    <small>
                      {[DEAL_MODEL[l.deal.model] ?? l.deal.model, l.months.length > 1 ? t(`${l.months.length} Monate`, `${l.months.length} months`) : null, l.deal.products_flat_taxed ? t("pauschal versteuert", "flat-rate taxed") : null]
                        .filter(Boolean).join(" · ")}
                    </small>
                  </td>
                  <td data-label="Status">
                    <PillView pill={l.pill} />
                    {terminal && l.active && l.openMonths > 0 && <small>{l.openMonths === 1 ? t("1 Monat offen", "1 month open") : t(`${l.openMonths} Monate offen`, `${l.openMonths} months open`)}</small>}
                  </td>
                  <td data-label={t("Laufzeit", "Term")} className="tk-text-umbruch" title={termLabel(l)}>{termLabel(l)}</td>
                  <td data-label={t("Nächste Frist", "Next deadline")}>{date(l.nextDeadline)}</td>
                  <td data-label={t("Plattformen", "Platforms")} className="tk-text-umbruch" title={platformsLabel(l.deal.platforms)}>{platformsLabel(l.deal.platforms)}</td>
                  {amounts && <td data-label={t("Honorar netto", "Net fee")}><Money cents={l.totalCents} currency={l.deal.currency} /></td>}
                </tr>
              );
            })}
            {sorted.length === 0 && (
              <tr><td colSpan={amounts ? 7 : 6}>{lines.length ? t("In dieser Ansicht gibt es keine Kooperationen.", "There are no deals in this view.") : empty}</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}

/**
 * Was die angemeldete Person an Geldangaben sieht (Befund 9): „Beträge sehen“ (Honorare, Rechnungsbeträge, Provisionssätze)
 * und „Kennzahlen und Agenturumsatz sehen“ (Agenturanteile, Umsatzsummen, Verläufe). Ohne Arbeitsbereich (Tests): alles.
 */
export function useSicht(): { amounts: boolean; financials: boolean } {
  let ws: ReturnType<typeof useStaffWorkspace> | null = null;
  try {
    ws = useStaffWorkspace();
  } catch {
    ws = null;   // außerhalb eines Agentur-Arbeitsbereichs (Einzeltests)
  }
  if (!ws) return { amounts: true, financials: true };
  return { amounts: ws.can("view_amounts"), financials: ws.can("view_financials") };
}
/**
 * Bei jeder Tarifgrenze der Weg zum größeren Tarif (Praxisfall „Tarifgrenze erreicht“): Admin wählt, alle anderen fragen ihn.
 * Für gesperrte Module der Tarifstaffel (0099) gibt es den Ausbau TarifHinweis (components/Tarif.tsx) mit dem Namen des Tarifs.
 */
export function TarifWaehlen() {
  const chef = !!useArbeitsbereich()?.isChef;
  return chef
    ? <> <UpgradeLink /></>
    : <> {t("Einen größeren Tarif wählt der Admin.", "The admin chooses a larger plan.")}</>;
}
/** K-374: „Upgrade auf <nächster Tarif>“, öffnet direkt den Wechseldialog; im größten Tarif „Größeren Tarif wählen“ zur Abo-Seite */
export function UpgradeLink() {
  const upgrade = useUpgradeZiel();
  return <Link to={upgrade?.to ?? "/agentur/einstellungen/abo"}>{upgrade?.label ?? t("Größeren Tarif wählen", "Choose a larger plan")}</Link>;
}

export const HIDDEN_AMOUNTS = () => t("Beträge sind für dein Konto ausgeblendet. Ein Admin vergibt das Recht unter Einstellungen · Team & Rechte.", "Amounts are hidden for your account. An admin grants the permission under Settings · Team & permissions.");
export const HIDDEN_FINANCIALS = () => t("Agentur-Einnahmen und Umsätze sind für dein Konto ausgeblendet (Recht „Kennzahlen und Agenturumsatz sehen“).", "Agency income and revenue are hidden for your account (“View metrics and agency revenue” permission).");

// ── Rechnungen, Verträge, Buchungen ──────────────────────────────────────────────────────────────

export function InvoiceTable({ views, partnerName, partnerLabel, caption, empty }: {
  views: InvoiceView[]; partnerName: (v: InvoiceView) => string; partnerLabel: string; caption: string; empty: string;
}) {
  const [all, setAll] = useState(false);
  const { amounts } = useSicht();
  const { sorted, sort, toggle } = useSorted(views, {
    nummer: (v) => v.row.issue_date,
    art: (v) => INVOICE_KIND[v.row.kind] ?? v.row.kind,
    partner: (v) => partnerName(v),
    status: (v) => v.pill.label,
    faellig: (v) => v.row.effective_due_date,
    betrag: (v) => v.row.gross_cents,
    saldo: (v) => balanceCents(v),
  });
  const list = all ? sorted : sorted.slice(0, 20);
  return (
    <>
      <SortWahl />
      <div className="tablewrap tk-cardtable">
        <table>
          <caption className="sr-only">{caption}</caption>
          <thead>
            <tr>
              <SortTh label={t("Rechnung / Datum", "Invoice / date")} k="nummer" sort={sort} toggle={toggle} />
              <SortTh label={t("Art", "Type")} k="art" sort={sort} toggle={toggle} />
              <SortTh label={partnerLabel} k="partner" sort={sort} toggle={toggle} />
              <SortTh label="Status" k="status" sort={sort} toggle={toggle} />
              <SortTh label={t("Fällig", "Due")} k="faellig" sort={sort} toggle={toggle} />
              {amounts && <SortTh label={t("Betrag brutto", "Gross amount")} k="betrag" sort={sort} toggle={toggle} money />}
              {amounts && <SortTh label={t("Saldo", "Balance")} k="saldo" sort={sort} toggle={toggle} money />}
            </tr>
          </thead>
          <tbody>
            {list.map((v) => (
              <tr key={v.row.id}>
                <td data-label={t("Rechnung / Datum", "Invoice / date")}>
                  <strong><Link to={`/agentur/rechnungen/${v.row.id}`}>{v.row.number ?? t("Ohne Nummer", "Without number")}</Link></strong>
                  <small>{date(v.row.issue_date)}</small>
                </td>
                <td data-label={t("Art", "Type")} className="tk-text-umbruch" title={INVOICE_KIND[v.row.kind] ?? v.row.kind}>{INVOICE_KIND[v.row.kind] ?? v.row.kind}</td>
                <td data-label={partnerLabel} className="tk-text-umbruch" title={partnerName(v)}>{partnerName(v)}</td>
                <td data-label="Status">
                  <PillView pill={v.pill} />
                  {v.state.isOverdue && <small>{t("seit ", "for ")}{v.state.daysOverdue} {v.state.daysOverdue === 1 ? t("Tag", "day") : t("Tagen", "days")} {t("überfällig · ", "overdue · ")}{actionLabel(v.next, v.row.kind)}</small>}
                </td>
                <td data-label={t("Fällig", "Due")}>{date(v.row.effective_due_date)}</td>
                {amounts && <td data-label={t("Betrag brutto", "Gross amount")}><Money cents={v.row.gross_cents} currency={v.row.currency} /></td>}
                {amounts && <td data-label={t("Saldo", "Balance")}><Money cents={balanceCents(v)} currency={v.row.currency} /></td>}
              </tr>
            ))}
            {views.length === 0 && <tr><td colSpan={amounts ? 7 : 5}>{empty}</td></tr>}
          </tbody>
        </table>
      </div>
      {views.length > 20 && (
        <div className="row">
          <button className="" onClick={() => setAll((x) => !x)}>{all ? t("Nur die neuesten 20 zeigen", "Show only the latest 20") : t(`Alle ${views.length} Rechnungen zeigen`, `Show all ${views.length} invoices`)}</button>
        </div>
      )}
    </>
  );
}

export function ContractList({ contracts, partnerName, empty }: { contracts: ContractRow[]; partnerName: (c: ContractRow) => string; empty: string }) {
  // K-511: Vorschau je Vertrag (Creator-Profil, Markendetail), offen über ?vorschau=<id>
  const vorschau = useVorschauParam();
  if (!contracts.length) return <p className="muted">{empty}</p>;
  return (
    <div>
      {contracts.slice(0, 10).map((c) => (
        <div key={c.id} className="tk-vv-zeile">
        <Link className="task" to={`/agentur/vertraege/${c.id}`}>
          <div>
            <strong>{c.title}</strong>
            <p>
              {[partnerName(c), c.contract_number ? t(`Nr. ${c.contract_number}`, `No. ${c.contract_number}`) : null, c.origin === "pdf_upload" ? t("PDF hochgeladen", "PDF uploaded") : null,
                c.completed_at ? t(`unterschrieben am ${date(c.completed_at)}`, `signed on ${date(c.completed_at)}`) : c.sent_at ? t(`versendet am ${date(c.sent_at)}`, `sent on ${date(c.sent_at)}`) : null]
                .filter(Boolean).join(" · ")}
            </p>
          </div>
          <PillView pill={contractPill(c)} />
        </Link>
        {c.origin !== "pdf_upload" && <button type="button" onClick={() => vorschau.open(c.id)} aria-label={t(`Vorschau: ${c.title}`, `Preview: ${c.title}`)}>{t("Vorschau", "Preview")}</button>}
        </div>
      ))}
      {contracts.length > 10 && <p className="source-note">{contracts.length - 10} {t("weitere unter Verträge.", "more under Contracts.")}</p>}
      <VertragVorschau contractId={vorschau.id && contracts.some((c) => c.id === vorschau.id) ? vorschau.id : null} onClose={vorschau.close} />
    </div>
  );
}

export function BookingTable({ bookings, partnerName, dealTitle, caption }: {
  bookings: BookingRow[]; partnerName: (b: BookingRow) => string; dealTitle: (b: BookingRow) => string | null; caption: string;
}) {
  const { amounts, financials } = useSicht();
  return (
    <div className="tablewrap tk-cardtable">
      <table>
        <caption className="sr-only">{caption}</caption>
        <thead>
          <tr>
            <th scope="col">{t("Datum", "Date")}</th>
            <th scope="col">{t("Art", "Type")}</th>
            <th scope="col">{t("Zuordnung", "Assignment")}</th>
            <th scope="col">{t("Notiz", "Note")}</th>
            <th scope="col">Status</th>
            {financials && <th scope="col" className="money">{t("Agentur-Einnahmen", "Agency income")}</th>}
            {amounts && <th scope="col" className="money">{t("Betrag", "Amount")}</th>}
          </tr>
        </thead>
        <tbody>
          {bookings.map((b) => {
            const pillOf: Pill = bookingPill(b);
            return (
              <tr key={b.id}>
                <td data-label={t("Datum", "Date")}>{date(b.booked_on)}</td>
                <td data-label={t("Art", "Type")}>{BOOKING_KIND[b.kind] ?? b.kind}</td>
                <td data-label={t("Zuordnung", "Assignment")}>{partnerName(b)}{dealTitle(b) && <small>{dealTitle(b)}</small>}</td>
                <td data-label={t("Notiz", "Note")}>{b.note ?? "–"}</td>
                <td data-label="Status"><PillView pill={pillOf} /></td>
                {financials && <td data-label={t("Agentur-Einnahmen", "Agency income")}><Money cents={b.agency_share_cents} currency={b.currency} /></td>}
                {amounts && <td data-label={t("Betrag", "Amount")}><Money cents={b.amount_cents} currency={b.currency} /></td>}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── Formularfelder (Beschriftung immer sichtbar über dem Feld, Fehler unter dem Feld) ─────────────

export function TextField({ id, label, value, onChange, error, hint, type = "text", required, placeholder, inputMode, autoComplete, disabled }: {
  id: string; label: string; value: string; onChange: (v: string) => void; error?: string; hint?: ReactNode; type?: string; required?: boolean;
  placeholder?: string; inputMode?: "text" | "decimal" | "numeric" | "email" | "tel"; autoComplete?: string; disabled?: boolean;
}) {
  const describedBy = [error ? `${id}-fehler` : null, hint ? `${id}-hinweis` : null].filter(Boolean).join(" ") || undefined;
  return (
    <label>
      {label}
      <input
        id={id} type={type} value={value} required={required} placeholder={placeholder} inputMode={inputMode} autoComplete={autoComplete ?? "off"} disabled={disabled}
        aria-invalid={error ? true : undefined} aria-describedby={describedBy} onChange={(e) => onChange(e.target.value)}
      />
      {hint && <small id={`${id}-hinweis`}>{hint}</small>}
      <FieldError id={`${id}-fehler`} text={error} />
    </label>
  );
}

export function SelectField({ id, label, value, onChange, options, error, hint, disabled }: {
  id: string; label: string; value: string; onChange: (v: string) => void; options: readonly { value: string; label: string }[]; error?: string; hint?: ReactNode; disabled?: boolean;
}) {
  const describedBy = [error ? `${id}-fehler` : null, hint ? `${id}-hinweis` : null].filter(Boolean).join(" ") || undefined;
  return (
    <label>
      {label}
      <select id={id} value={value} disabled={disabled} aria-invalid={error ? true : undefined} aria-describedby={describedBy} onChange={(e) => onChange(e.target.value)}>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      {hint && <small id={`${id}-hinweis`}>{hint}</small>}
      <FieldError id={`${id}-fehler`} text={error} />
    </label>
  );
}
